"use client";

import AuthorizedDistributorBadge from "@/components/authorized-distributor-badge/authorizedDistributorBadge";
import CurrencyDisplay from "@/app/components/currencyDisplay";
import {
  ArrivalDateVariation,
  HandlingTimeVariation,
} from "@/app/ec/product/[basePath]/vendor-options/arrivalDateAbTest";
import getArrivalDateLabel from "@/app/ec/product/[basePath]/vendor-options/getArrivalDateLabel";
import getChosenPriceBreakExpiration from "@/app/ec/product/[basePath]/vendor-options/getChosenPriceBreakExpiration";
import getStockedMessage, { StockedState } from "@/app/ec/product/[basePath]/vendor-options/getStockedMessage";
import getVendorNameForURL from "@/app/vendor/utils/getVendorNameForURL";
import Link from "next/link";
import CheckIcon from "public/next-public/common/icons/checkmark-bold-icon.svg";
import DollarIcon from "public/next-public/common/icons/dollar-sign-icon.svg";
import LightningIcon from "public/next-public/common/icons/lightning-bolt-icon.svg";
import StarIcon from "public/next-public/common/icons/star-solid-icon.svg";
import TruckIcon from "public/next-public/common/icons/shipping-truck.svg";
import TruckLightIcon from "public/next-public/common/icons/truck-light-icon.svg";
import WarningIcon from "public/next-public/common/icons/warning-icon.svg";
import { ComponentType, FunctionComponent, SVGProps, useMemo } from "react";
import style from "./buyBoxTile.module.scss";
import { HeavyShippingStatus } from "./configureVendorOptions";

export type BuyBoxTileCategory = "default" | "authorized" | "lowest-price" | "fastest-shipping";

export type BuyBoxTileProps = {
  category: BuyBoxTileCategory;
  vendorOption: VendorOptionState | null;
  isSelected: boolean;
  isDisabled?: boolean;
  disabledReason?: string;
  quantity: number;
  onSelect: () => void;
};

const BuyBoxTile: FunctionComponent<BuyBoxTileProps> = ({
  category,
  vendorOption,
  isSelected,
  isDisabled = false,
  disabledReason,
  quantity,
  onSelect,
}) => {
  const categoryConfig = useMemo<{
    label: string;
    Icon: ComponentType<SVGProps<SVGSVGElement>> | null;
    iconAlt: string;
  }>(() => {
    switch (category) {
      case "default":
        return {
          label: "Featured",
          Icon: StarIcon,
          iconAlt: "Featured",
        };
      case "authorized":
        return {
          label: "Authorized Distributor",
          Icon: CheckIcon,
          iconAlt: "Authorized distributor",
        };
      case "lowest-price":
        return {
          label: "Lowest Price",
          Icon: DollarIcon,
          iconAlt: "Lowest price",
        };
      case "fastest-shipping":
        return {
          label: "Fastest Shipping",
          Icon: LightningIcon,
          iconAlt: "Fastest shipping",
        };
      default:
        return {
          label: "",
          Icon: null,
          iconAlt: "",
        };
    }
  }, [category]);

  const shippingDays = useMemo(() => {
    if (!vendorOption || vendorOption.isBackordered === true) {
      return null;
    } else if (vendorOption.shippingTime >= 30) {
      return "30+";
    } else {
      return vendorOption.shippingTime;
    }
  }, [vendorOption]);

  const hasLongHandlingTime = useMemo(() => {
    if (!vendorOption || shippingDays == null) return false;
    const stockState = getStockedMessage(vendorOption);
    return stockState === StockedState.LONG_HANDLING_TIME;
  }, [vendorOption, shippingDays]);

  const isBackordered = vendorOption?.isBackordered === true;

  const arrivalDateLabel = useMemo(() => getArrivalDateLabel(vendorOption), [vendorOption]);
  const hasArrivalDateVariation = isBackordered || arrivalDateLabel !== null;

  const arrivalDateValue = (
    <span
      className={`${style["tile-arrival-date"]} ${
        isBackordered || hasLongHandlingTime ? style["tile-arrival-date-warning"] : ""
      }`}
    >
      {isBackordered ? "Backordered" : arrivalDateLabel}
    </span>
  );

  const hasHeavyShipping = useMemo(() => {
    if (!vendorOption) return false;
    return (
      vendorOption.heavyShippingStatus === HeavyShippingStatus.HAS_KNOWN_HEAVY_SHIPPING &&
      vendorOption.heavyShipping > 0
    );
  }, [vendorOption]);

  const expirationDate = useMemo(() => getChosenPriceBreakExpiration(vendorOption), [vendorOption]);

  const priceParts = useMemo(() => {
    if (!vendorOption) {
      return { dollars: null, cents: null };
    }
    const formattedPrice = vendorOption.productGrandTotal.toFixed(2);
    const [dollars, cents = null] = formattedPrice.split(".");

    return { dollars, cents };
  }, [vendorOption, isSelected]);

  const shippingDisplay = useMemo(() => {
    if (!vendorOption) return null;

    const days = shippingDays;
    if (days === null || days === undefined) {
      if (isSelected && vendorOption.vendorRegion) {
        return `Ships from ${vendorOption.vendorRegion}`;
      }
      return null;
    }

    if (isSelected) {
      const daysText = days === 1 ? "business day" : "business days";
      return `Ships from ${vendorOption.vendorRegion} in ${days} ${daysText}`;
    } else {
      return `${days} day${days !== 1 ? "s" : ""}`;
    }
  }, [vendorOption, shippingDays, isSelected]);

  const vendorNameForURL = useMemo<string>(() => {
    if (!vendorOption) return "";
    return getVendorNameForURL(vendorOption.vendorName);
  }, [vendorOption]);

  if (!vendorOption && category !== "authorized") {
    return null;
  }

  const CategoryIcon = categoryConfig.Icon;

  return (
    <div
      className={`${style["buy-box-tile"]} ${isSelected ? style["tile-selected"] : ""} ${
        isDisabled ? style["tile-disabled"] : ""
      }`}
      onClick={!isDisabled ? onSelect : undefined}
      role="radio"
      aria-checked={isSelected}
      aria-disabled={isDisabled}
      tabIndex={isDisabled ? -1 : 0}
      onKeyDown={(e) => {
        if (!isDisabled && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      <div className={style["tile-radio-container"]}>
        <div
          className={`${style["tile-radio"]} ${isSelected ? style["radio-selected"] : ""} ${
            isDisabled ? style["radio-disabled"] : ""
          } radio-${category}`}
        >
          {isSelected && <div className={style["radio-selected-dot"]}></div>}
        </div>
      </div>

      <div className={style["tile-content"]}>
        <div className={style["tile-header"]}>
          <div className={style["tile-header-left"]}>
            {CategoryIcon && <CategoryIcon className={style["tile-icon-img"]} width={20} height={20} aria-hidden />}

            <span className={style["tile-label"]}>{categoryConfig.label}</span>

            {!isDisabled && !isSelected && (shippingDays != null || isBackordered) && (
              <HandlingTimeVariation enabled={hasArrivalDateVariation}>
                <span
                  className={`${style["tile-shipping-badge"]} ${hasLongHandlingTime ? style["tile-long-handling"] : ""} ${
                    isBackordered ? style["tile-backordered"] : ""
                  }`}
                >
                  <TruckIcon className={style["shipping-icon"]} width={16} height={16} aria-hidden />
                  {isBackordered ? "Backordered" : shippingDisplay}
                </span>
              </HandlingTimeVariation>
            )}

            {!isDisabled && !isSelected && hasHeavyShipping && (
              <WarningIcon className={style["warning-icon"]} width={16} height={16} aria-hidden />
            )}
          </div>

          {!isDisabled && (
            <div className={style["tile-price"]}>
              {priceParts.dollars && <CurrencyDisplay value={priceParts.dollars} decimalScale={0} />}
              {priceParts?.cents && <span className={style["price-cents"]}>{priceParts.cents}</span>}
            </div>
          )}

          {isDisabled && disabledReason && <div className={style["tile-disabled-message"]}>{disabledReason}</div>}
        </div>

        {!isDisabled && !isSelected && hasArrivalDateVariation && (
          <ArrivalDateVariation as="div">
            <div className={style["tile-arrival"]}>
              <TruckLightIcon className={style["tile-arrival-icon"]} width={16} height={16} aria-hidden />
              <span className={style["tile-arrival-label"]}>Est. Delivery:</span>
              {arrivalDateValue}
            </div>
          </ArrivalDateVariation>
        )}

        {isSelected && vendorOption && (
          <div className={style["tile-selected-content"]}>
            <HandlingTimeVariation as="div" enabled={hasArrivalDateVariation}>
              <div
                className={`${hasLongHandlingTime ? style["tile-long-handling-wrapper"] : ""} ${
                  isBackordered ? style["tile-backordered-wrapper"] : ""
                }`}
              >
                {shippingDisplay ? (
                  <div
                    className={`${style["tile-shipping-full"]} ${hasLongHandlingTime ? style["tile-long-handling"] : ""} ${
                      isBackordered ? style["tile-backordered"] : ""
                    }`}
                  >
                    <TruckIcon className={style["shipping-icon"]} width={20} height={20} aria-hidden />
                    {shippingDisplay}
                  </div>
                ) : (
                  isBackordered && (
                    <span className={`${style["tile-shipping-badge"]} ${style["tile-backordered"]}`}>
                      <TruckIcon className={style["shipping-icon"]} width={16} height={16} aria-hidden />
                      Backordered
                    </span>
                  )
                )}

                {hasLongHandlingTime && <div className={style["tile-long-handling-warning"]}>Long handling time</div>}
                {isBackordered && shippingDisplay && (
                  <div className={style["tile-backordered-warning"]}>Backordered</div>
                )}
              </div>
            </HandlingTimeVariation>

            {expirationDate && (
              <div className={`${style["tile-warning-message"]} ${style["tile-expiration-message"]}`}>
                <WarningIcon className={style["warning-icon"]} width={16} height={16} aria-hidden />
                Product expires: {expirationDate}
              </div>
            )}

            {hasHeavyShipping && (
              <div className={style["tile-warning-message"]}>
                <WarningIcon className={style["warning-icon"]} width={16} height={16} aria-hidden />
                Heavy shipping surcharge <CurrencyDisplay value={vendorOption.heavyShipping.toFixed(2)} />
              </div>
            )}

            {hasArrivalDateVariation && (
              <ArrivalDateVariation as="div">
                <div className={`${style["tile-arrival"]} ${style["tile-arrival-full"]}`}>
                  <TruckLightIcon className={style["tile-arrival-icon"]} width={16} height={16} aria-hidden />
                  <span className={style["tile-arrival-label"]}>
                    {vendorOption.vendorRegion
                      ? `Estimated Delivery from ${vendorOption.vendorRegion}:`
                      : "Estimated Delivery:"}
                  </span>
                  {arrivalDateValue}
                  {hasLongHandlingTime && (
                    <span className={style["tile-arrival-long-handling"]}>(Long handling time)</span>
                  )}
                </div>
              </ArrivalDateVariation>
            )}

            <div className={style["tile-vendor-info"]}>
              <div className={style["sold-by"]}>
                Sold by
                <Link
                  className={style["sold-by-link"]}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={`Vendor Product Code (VP Code): ${vendorOption.vendorProductCode}`}
                  href={`/vendor/${vendorNameForURL}/${vendorOption.vendorId}`}
                  prefetch={false}
                >
                  <span>{vendorOption.vendorName}</span>
                </Link>
              </div>
              {vendorOption.isAuthorizedDistributor && <AuthorizedDistributorBadge />}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BuyBoxTile;
